import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "../../lib/hooks";

/**
 * Editorial Modal primitive — centered card on desktop, bottom sheet on mobile.
 *
 * Designed as part of the printed-plate motif, NOT a generic chat-app dialog.
 * - Vellum-tinted ink backdrop with paper-grain noise overlay
 * - Display-italic × close glyph (no chrome)
 * - Violet-glow shadow stack pairing with the brand's image-frame outline
 * - Gold marginalia rule above the body
 * - Staggered open animation (panel → title → rule-violet)
 *
 * Reduced motion collapses all animation to opacity-only fades.
 *
 * Future variants (drawer-right, drawer-bottom) can be introduced by adding a
 * `variant` prop and corresponding CSS — `BlockPalette` and `RevisionDrawer`
 * are good migration candidates but out of scope for this refactor.
 */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Required for `aria-labelledby`. Use `titleVisuallyHidden` to suppress visual rendering. */
  title: string;
  titleVisuallyHidden?: boolean;
  /** Small caps label rendered above the title. */
  eyebrow?: string;
  /** Right-aligned slot for actions like Edit / View full / Duplicate. */
  headerActions?: React.ReactNode;
  /** Desktop max-width: 'md' = 720px, 'lg' = 920px, 'xl' = 1080px. Default 'lg'. */
  size?: "md" | "lg" | "xl";
  /** When true (default) the modal renders as a bottom sheet on phones. */
  fullScreenOnMobile?: boolean;
  /** Element to focus on open. Defaults to the close button. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  ariaDescribedById?: string;
  /** Override for unsaved-form confirms. Defaults to onClose. */
  onBackdropClick?: () => void;
  children: React.ReactNode;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const CLOSE_DURATION_MS = 200;

export function Modal({
  open,
  onClose,
  title,
  titleVisuallyHidden = false,
  eyebrow,
  headerActions,
  size = "lg",
  fullScreenOnMobile = true,
  initialFocusRef,
  ariaDescribedById,
  onBackdropClick,
  children,
}: ModalProps) {
  const titleId = useId();
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<"open" | "closing">(open ? "open" : "closing");
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Mount + unmount with animation. When open flips to true, render immediately.
  // When open flips to false, transition to "closing" then unmount after the
  // animation duration.
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Defer to next frame so the initial paint is in the "closing"-positioned
      // state, then transitions to "open" — this is what triggers the entrance.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setState("open"));
      });
    } else if (mounted) {
      setState("closing");
      const t = window.setTimeout(() => setMounted(false), CLOSE_DURATION_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  useScrollLock(mounted);

  // Save / restore focus + initial focus + Escape + focus trap.
  useEffect(() => {
    if (!mounted) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    // Defer focus until the panel is in the DOM and animations have started.
    const focusTarget = initialFocusRef?.current ?? closeButtonRef.current;
    requestAnimationFrame(() => focusTarget?.focus());

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      previousFocusRef.current?.focus?.();
    };
  }, [mounted, onClose, initialFocusRef]);

  if (!mounted) return null;

  const overlay = (
    <div
      className="modal-overlay"
      data-state={state}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={ariaDescribedById}
    >
      <button
        type="button"
        className="modal-backdrop"
        aria-label="Close"
        onClick={onBackdropClick ?? onClose}
        tabIndex={-1}
      />
      <div
        ref={panelRef}
        className="modal-panel"
        data-size={size}
        data-mobile={fullScreenOnMobile ? "sheet" : "card"}
      >
        <header className="modal-header">
          {fullScreenOnMobile && <span className="modal-grabber" aria-hidden />}
          <div className="modal-header-text">
            {eyebrow && <p className="modal-eyebrow">{eyebrow}</p>}
            {titleVisuallyHidden ? (
              <h2 id={titleId} className="sr-only">
                {title}
              </h2>
            ) : (
              <h2 id={titleId} className="modal-title">
                {title}
              </h2>
            )}
            <span className="modal-rule" aria-hidden />
          </div>
          {headerActions && <div className="modal-header-actions">{headerActions}</div>}
          <button
            ref={closeButtonRef}
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <span aria-hidden>×</span>
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
